// Rotas do app único (DESIGN §7): "/" aluno (PWA), "/painel" equipe, "/checkin" (QR da recepção).
// Toda tela é lazy (um chunk por tela). Os NOMES de caminho são contrato com links de push/avisos
// (SPEC §4.12 `link`) — mudar aqui exige avisar o backend.
import type { ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from "react-router";
import "./aluno/temaInicial";
import { Vazio, BotaoLink } from "./compartilhado/ui";
import { ErroRota } from "./ErroRota";
import { ExigirAluno, SomenteVisitante, Splash } from "./guardas";
import { tela } from "./rotas.comum";
import { LEGADO_ALUNO, rotasLegado } from "./rotas.legado";
import { criarRotasPainel } from "./rotas.painel";
// Por último: no web o módulo nem é usado (`__ALVO__`), e a ordem dos outros imports fica a de sempre no bundle.
import { RaizApp } from "@plataforma/raiz";
import { sorteiosVisiveis } from "@plataforma/recursos";

function NaoEncontrada() {
  return (
    <div className="grid min-h-dvh place-items-center bg-parede px-margem">
      <Vazio
        titulo="Página não encontrada"
        texto="O endereço pode ter mudado. Volte para o início."
        acao={
          <BotaoLink to="/" variante="secundario">
            Ir para o início
          </BotaoLink>
        }
      />
    </div>
  );
}

// ── Aluno (dono: frontend-aluno). Layouts também são lazy: o bundle inicial do painel não carrega o aluno.
type ModuloLayout = { default: ComponentType };
const layoutAluno = (
  nome: "LayoutFluxoAluno" | "LayoutPublicoAluno",
): Pick<RouteObject, "lazy" | "errorElement"> =>
  tela(() => import("./aluno/LayoutAluno").then((m): ModuloLayout => ({ default: m[nome] })));

/**
 * Sorteios: nos apps das lojas, iOS e Android (envio-apple-estados §5), as rotas saem da árvore; quem chega por link
 * universal ou push vai para o Ranking com `replace`. Web: a lista (/sorteios -> segmento do Ranking) e o detalhe.
 */
function rotasSorteios(): RouteObject[] {
  if (!sorteiosVisiveis())
    return [
      { path: "/sorteios", element: <Navigate to="/ranking" replace /> },
      { path: "/sorteios/*", element: <Navigate to="/ranking" replace /> },
    ];
  return [
    { path: "/sorteios", element: <Navigate to="/ranking?aba=sorteios" replace /> },
    { path: "/sorteios/:id", ...tela(() => import("./aluno/ranking/SorteioDetalhe")) },
  ];
}

const rotasAluno = (): RouteObject[] => [
  // Caminhos antigos de links de avisos: só redirecionam (o destino aplica o guarda)
  ...rotasLegado(LEGADO_ALUNO),
  // Público (sem login): formulário de indicação
  {
    ...layoutAluno("LayoutPublicoAluno"),
    children: [{ path: "/indicar/:codigo", ...tela(() => import("./aluno/indicacoes/IndicarPublico")) }],
  },
  // Acesso (visitante)
  {
    element: <SomenteVisitante area="aluno" />,
    children: [
      {
        ...layoutAluno("LayoutPublicoAluno"),
        children: [
          { path: "/entrar", ...tela(() => import("./aluno/acesso/Entrar")) },
          { path: "/primeiro-acesso", ...tela(() => import("./aluno/acesso/PrimeiroAcesso")) },
          // Senha própria por código no e-mail (docs/prototipo/senha-propria-aluno.html): criar e esqueci
          { path: "/senha", ...tela(() => import("./aluno/acesso/SenhaPropria")) },
        ],
      },
    ],
  },
  // "Pronto" da senha própria (QA 6): já logado, fora do guarda de visitante; grava a sessão ao abrir.
  {
    ...layoutAluno("LayoutPublicoAluno"),
    children: [{ path: "/senha/pronto", ...tela(() => import("./aluno/acesso/ProntoSenha")) }],
  },
  // Onboarding e instalação (logado, antes de concluir o cadastro)
  {
    element: <ExigirAluno permitirOnboarding />,
    children: [
      {
        ...layoutAluno("LayoutFluxoAluno"),
        children: [
          { path: "/boas-vindas", element: <Navigate to="/boas-vindas/1" replace /> },
          { path: "/boas-vindas/:passo", ...tela(() => import("./aluno/onboarding/Onboarding")) },
          { path: "/instalar", ...tela(() => import("./aluno/onboarding/Instalar")) },
        ],
      },
    ],
  },
  // App do aluno
  {
    element: <ExigirAluno />,
    children: [
      // Com tab bar: Início · Treino · Comunidade · Ranking · Perfil (DESIGN §0.5)
      {
        ...tela(() => import("./aluno/LayoutAluno")),
        children: [
          { index: true, path: "/", ...tela(() => import("./aluno/inicio/Inicio")) },
          { path: "/avisos", ...tela(() => import("./aluno/avisos/Avisos")) },
          { path: "/treino", ...tela(() => import("./aluno/treino/Ficha")) },
          { path: "/comunidade", ...tela(() => import("./aluno/comunidade/Comunidade")) },
          { path: "/comunidade/post/:id", ...tela(() => import("./aluno/comunidade/PostDetalhe")) },
          { path: "/comunidade/dicas/:id", ...tela(() => import("./aluno/comunidade/PostDetalhe")) },
          { path: "/comunidade/eventos", element: <Navigate to="/comunidade?aba=eventos" replace /> },
          { path: "/comunidade/eventos/:id", ...tela(() => import("./aluno/eventos/EventoDetalhe")) },
          { path: "/comunidade/dicas", element: <Navigate to="/comunidade?aba=dicas" replace /> },
          { path: "/ranking", ...tela(() => import("./aluno/ranking/Ranking")) },
          { path: "/desafios", element: <Navigate to="/ranking?aba=desafios" replace /> },
          { path: "/desafios/:id", ...tela(() => import("./aluno/desafios/DesafioDetalhe")) },
          ...rotasSorteios(),
          { path: "/historico", ...tela(() => import("./aluno/historico/Historico")) },
          { path: "/perfil", ...tela(() => import("./aluno/perfil/Perfil")) },
          { path: "/perfil/extrato", ...tela(() => import("./aluno/extrato/Extrato")) },
          { path: "/perfil/premios", ...tela(() => import("./aluno/premios/MeusPremios")) },
          { path: "/perfil/conquistas", element: <Navigate to="/perfil/medalhas?aba=conquistas" replace /> },
          { path: "/perfil/medalhas", ...tela(() => import("./aluno/medalhas/Medalhas")) },
          { path: "/perfil/privacidade", ...tela(() => import("./aluno/perfil/Privacidade")) },
          { path: "/perfil/privacidade/bloqueados", ...tela(() => import("./aluno/perfil/PessoasBloqueadas")) },
          { path: "/perfil/notificacoes", ...tela(() => import("./aluno/perfil/Notificacoes")) },
          { path: "/perfil/objetivo", ...tela(() => import("./aluno/perfil/Objetivo")) },
          { path: "/perfil/evolucao", ...tela(() => import("./aluno/evolucao/Evolucao")) },
          { path: "/perfil/indicacoes", ...tela(() => import("./aluno/indicacoes/Indicacoes")) },
          { path: "/perfil/feedback", ...tela(() => import("./aluno/feedback/Feedback")) },
          { path: "/perfil/pontos-extras", ...tela(() => import("./aluno/pontos/GanhePontos")) },
          { path: "/perfil/fidelidade", ...tela(() => import("./aluno/pontos/Fidelidade")) },
          { path: "/perfil/convites", ...tela(() => import("./aluno/convites/Convites")) },
        ],
      },
      // Fluxos sem tab bar
      {
        ...layoutAluno("LayoutFluxoAluno"),
        children: [
          { path: "/checkin", ...tela(() => import("./aluno/checkin/Checkin")) },
          { path: "/eventos/:id/presenca", ...tela(() => import("./aluno/eventos/PresencaEvento")) },
          { path: "/treino/concluido/:sessaoId", ...tela(() => import("./aluno/treino/Celebracao")) },
          { path: "/treino/:grupo", ...tela(() => import("./aluno/treino/Execucao")) },
          { path: "/mensagens", ...tela(() => import("./aluno/mensagens/Mensagens")) },
          { path: "/comunidade/post/novo", ...tela(() => import("./aluno/comunidade/NovoPost")) },
          { path: "/perfil/feedback/novo", ...tela(() => import("./aluno/feedback/NovoFeedback")) },
          // Perfil › Senha e segurança (senha própria; sem tab bar, como no mockup)
          { path: "/perfil/seguranca", ...tela(() => import("./aluno/perfil/Seguranca")) },
          { path: "/perfil/seguranca/trocar", ...tela(() => import("./aluno/perfil/TrocarSenha")) },
          { path: "/perfil/seguranca/criar", ...tela(() => import("./aluno/perfil/CriarSenhaPerfil")) },
        ],
      },
    ],
  },
];

// ── Por alvo. `__ALVO__` é constante de build (vite.config.ts), então só um dos ramos existe em cada bundle.
// Web: o painel (rotas.painel.tsx). App nativo (ADR app nativo, D6 e D7): o painel some do bundle e qualquer
// /painel* cai em "O painel da equipe abre no navegador"; entra "Qual é a sua academia?", antes do login,
// "Sobre este app" (versões, ID do aparelho, procurar atualização; live update, Fase 4) e "Receba os avisos da
// academia" (explicação antes da permissão de notificações, Fase 5; no lugar do /instalar do web).
const rotasDoAlvo: RouteObject[] =
  __ALVO__ === "nativo"
    ? [
        { path: "/painel/*", ...tela(() => import("./plataforma/nativo/telas/PainelNoNavegador")) },
        { path: "/sobre-o-app", ...tela(() => import("./plataforma/nativo/telas/SobreApp")) },
        {
          element: <ExigirAluno permitirOnboarding />,
          children: [
            { path: "/ativar-avisos", ...tela(() => import("./plataforma/nativo/telas/AtivarAvisos")) },
          ],
        },
        {
          element: <SomenteVisitante area="aluno" />,
          children: [
            {
              ...layoutAluno("LayoutPublicoAluno"),
              children: [
                { path: "/academia", ...tela(() => import("./plataforma/nativo/telas/SeletorAcademia")) },
              ],
            },
          ],
        },
      ]
    : criarRotasPainel();

/** Árvore de rotas do app (exportada para o teste dos links de avisos, rotas.links-avisos.test.ts). */
export function criarRotas(): RouteObject[] {
  return [
    {
      // App: a raiz também recebe os links que o sistema abre no app (Universal/App Links, Fase 5).
      element: __ALVO__ === "nativo" ? <RaizApp /> : <Outlet />,
      hydrateFallbackElement: <Splash />,
      errorElement: <ErroRota />,
      children: [...rotasAluno(), ...rotasDoAlvo, { path: "*", element: <NaoEncontrada /> }],
    },
  ];
}

export function criarRoteador() {
  return createBrowserRouter(criarRotas());
}

export type Roteador = ReturnType<typeof criarRoteador>;
